'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { X, Upload, Loader2, Eye, FileText } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { presentationService } from '@/lib/services/presentationService';

interface SessionUploadPopupProps {
  userId: string;
  userEmail: string;
  userName: string;
  onClose: () => void;
}

interface UploadFormValues {
  title: string;
  sessionAims: string;
  deliveryTips: string;
}

function formatFileSize(bytes: number): string {
  const kb = bytes / 1024;
  if (kb >= 1024) return `${(kb / 1024).toFixed(1)} MB`;
  return `${Math.round(kb)} KB`;
}

const ACCEPTED_TYPES = [
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'video/mp4',
  'video/quicktime',
  'application/pdf',
];
const ACCEPTED_EXTS = ['.ppt', '.pptx', '.mp4', '.mov', '.pdf'];

const CATEGORY_META: Record<string, { accent: string; bg: string; border: string; dot: string; selectedAccent: string }> = {
  Psych: {
    accent: '#469dbd',
    bg: 'hsl(200 45% 97%)',
    border: 'hsl(200 35% 88%)',
    dot: '#469dbd',
    selectedAccent: '#2e7a96',
  },
  Age: {
    accent: '#7c3aed',
    bg: 'hsl(262 40% 97%)',
    border: 'hsl(262 30% 88%)',
    dot: '#7c3aed',
    selectedAccent: '#5b21b6',
  },
  Sport: {
    accent: '#16a34a',
    bg: 'hsl(142 40% 97%)',
    border: 'hsl(142 30% 88%)',
    dot: '#16a34a',
    selectedAccent: '#15803d',
  },
};

const DEFAULT_PSYCH_TAGS = ['Confidence', 'Anxiety', 'Focus', 'Motivation', 'Resilience', 'Mindfulness', 'Goal Setting', 'Self-Talk', 'Pressure', 'Arousal'];
const DEFAULT_AGE_TAGS = ['U10', 'U12', 'U14', 'U16', 'U18', 'Adult', 'Senior'];
const DEFAULT_SPORT_TAGS = ['Football', 'Rugby', 'Tennis', 'Golf', 'Athletics', 'Swimming', 'Cricket', 'Basketball', 'General'];

export default function SessionUploadPopup({ userId, userEmail, userName, onClose }: SessionUploadPopupProps) {
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<UploadFormValues | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [psychTags, setPsychTags] = useState<string[]>(DEFAULT_PSYCH_TAGS);
  const [ageTags, setAgeTags] = useState<string[]>(DEFAULT_AGE_TAGS);
  const [sportTags, setSportTags] = useState<string[]>(DEFAULT_SPORT_TAGS);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [showExamplePreview, setShowExamplePreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UploadFormValues>({ defaultValues: { title: '', sessionAims: '', deliveryTips: '' } });

  useEffect(() => {
    const loadTags = async () => {
      try {
        const tags = await presentationService.loadLibraryTags();
        if (tags.psych.length > 0) setPsychTags(tags.psych);
        if (tags.age.length > 0) setAgeTags(tags.age);
        if (tags.sport.length > 0) setSportTags(tags.sport);
      } catch {
        // fall back to defaults
      }
    };
    loadTags();
  }, []);

  const handleFileSelect = (file: File) => {
    setFileError(null);
    const hasValidExt = ACCEPTED_EXTS.some((ext) => file.name.toLowerCase().endsWith(ext));
    if (!ACCEPTED_TYPES.includes(file.type) && !hasValidExt) {
      setFileError('Accepted formats: .ppt, .pptx, .pdf, .mp4, .mov');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      setFileError('File is too large. Maximum size is 100 MB.');
      return;
    }
    setSelectedFile(file);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, []);

  const toggleCategory = (cat: string) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const onSubmit = async (data: UploadFormValues) => {
    if (!selectedFile) {
      setFileError('Please select a file to upload.');
      return;
    }
    setPendingFormData(data);
    setShowConfirm(true);
  };

  const handleConfirmSubmit = async () => {
    if (!pendingFormData || !selectedFile) return;
    setShowConfirm(false);
    setIsUploading(true);
    setUploadError(null);

    const supabase = createClient();
    try {
      const filePath = `${userId}/${Date.now()}_${selectedFile.name}`;

      const { error: storageError } = await supabase.storage
        .from('session-uploads')
        .upload(filePath, selectedFile, { upsert: false });

      if (storageError) throw storageError;

      const { data: urlData } = supabase.storage
        .from('session-uploads')
        .getPublicUrl(filePath);

      const fileUrl = urlData?.publicUrl ?? '';

      const { error: dbError } = await supabase.from('session_uploads').insert({
        uploader_id: userId,
        uploader_name: userName,
        uploader_email: userEmail,
        title: pendingFormData.title,
        description: pendingFormData.sessionAims,
        file_path: filePath,
        file_url: fileUrl,
        file_name: selectedFile.name,
        file_size_kb: Math.round(selectedFile.size / 1024),
        status: 'pending',
      });

      if (dbError) throw dbError;

      setUploadSuccess(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Upload failed. Please try again.';
      setUploadError(message);
    } finally {
      setIsUploading(false);
      setPendingFormData(null);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
    setPendingFormData(null);
  };

  const handleUploadAnother = () => {
    setUploadSuccess(false);
    setSelectedFile(null);
    setFileError(null);
    setUploadError(null);
    setSelectedCategories([]);
    setPendingFormData(null);
  };

  const renderTagGroup = (label: string, tags: string[]) => {
    if (tags.length === 0) return null;
    const meta = CATEGORY_META[label] ?? CATEGORY_META['Psych'];
    return (
      <div key={label}>
        <div
          className="flex items-center gap-1.5 mb-1 pb-1"
          style={{ borderBottom: `1px solid ${meta.border}` }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: meta.dot }}
          />
          <span
            className="font-bold uppercase tracking-widest"
            style={{ color: meta.accent, letterSpacing: '0.10em', fontSize: '9px' }}
          >
            {label}
          </span>
        </div>
        <div className="flex flex-wrap gap-1 mb-1.5">
          {tags.map((tag) => {
            const isSelected = selectedCategories.includes(tag);
            const baseColor = isSelected ? meta.selectedAccent : meta.accent;
            const hoverColor = meta.selectedAccent;
            return (
              <div
                key={`upload-tag-${tag}`}
                className="inline-flex items-center rounded-md font-semibold transition-all duration-150 whitespace-nowrap"
                style={{
                  height: '15px',
                  fontSize: '9px',
                  backgroundColor: baseColor,
                  border: `1px solid ${baseColor}`,
                  color: 'white',
                  boxShadow: isSelected ? '0 1px 4px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.18)',
                  flexShrink: 0,
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleCategory(tag)}
                  className="inline-flex items-center gap-0.5 h-full rounded-md"
                  style={{ color: 'white', padding: '0 6px' }}
                  onMouseEnter={(e) => {
                    const parent = (e.currentTarget as HTMLElement).parentElement;
                    if (parent) {
                      (parent as HTMLElement).style.backgroundColor = hoverColor;
                      (parent as HTMLElement).style.borderColor = hoverColor;
                      (parent as HTMLElement).style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    const parent = (e.currentTarget as HTMLElement).parentElement;
                    if (parent) {
                      (parent as HTMLElement).style.backgroundColor = baseColor;
                      (parent as HTMLElement).style.borderColor = baseColor;
                      (parent as HTMLElement).style.boxShadow = isSelected ? '0 1px 4px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.18)';
                    }
                  }}
                >
                  {isSelected && (
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" className="flex-shrink-0">
                      <path d="M1.5 4L3.5 6L6.5 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                  <span>{tag}</span>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 animate-fade-in"
      style={{ backgroundColor: 'rgb(0 0 0 / 0.65)' }}
    >
      <div
        className="w-full rounded-2xl overflow-hidden animate-scale-in flex flex-col relative"
        style={{
          maxWidth: '989px',
          height: '90vh',
          maxHeight: '90vh',
          backgroundImage: 'url(/assets/images/WHO_ARE_PEAK__26_-1781714641909.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          border: '1px solid hsl(145 40% 52%)',
          boxShadow: '0 25px 80px -10px rgb(0 0 0 / 0.6)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-3 border-b flex-shrink-0 relative"
          style={{ borderColor: 'hsl(145 40% 40%)' }}
        >
          <div className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center">
            <img
              src="/assets/images/WHATSAPP_PROFILE_PHOTOS__34_-1782925525799.png"
              alt="PEAK Vault Logo"
              style={{ height: '32px', width: 'auto', objectFit: 'contain' }}
            />
          </div>
          <div>
            <h2 className="text-lg font-bold" style={{ color: 'hsl(145 30% 94%)' }}>
              {uploadSuccess ? 'Session Uploaded' : 'Upload Session'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {!uploadSuccess && (
              <button
                type="button"
                onClick={() => setShowExamplePreview(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-bold transition-all duration-150 active:scale-95"
                style={{
                  backgroundColor: 'rgba(70,157,189,0.25)',
                  color: 'hsl(145 30% 94%)',
                  border: '1px solid rgba(70,157,189,0.5)',
                  fontSize: '0.72rem',
                  letterSpacing: '0.04em',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(70,157,189,0.4)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(70,157,189,0.25)';
                }}
              >
                <Eye size={12} />
                SEE EXAMPLE
              </button>
            )}
            {!isUploading && (
              <button
                onClick={onClose}
                className="p-2 rounded-lg transition-colors"
                style={{ color: 'hsl(145 20% 80%)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'hsl(145 40% 40%)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
                aria-label="Close upload modal"
              >
                <X size={17} />
              </button>
            )}
          </div>
        </div>

        {/* SUCCESS STATE */}
        {uploadSuccess ? (
          <div className="flex flex-col items-center justify-center flex-1 px-8 py-12 text-center">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
              style={{ backgroundColor: 'hsl(145 50% 30%)', border: '2px solid hsl(145 50% 50%)' }}
            >
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <path d="M8 20L16 28L32 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            <h3 className="text-2xl font-bold mb-3" style={{ color: 'hsl(145 30% 94%)' }}>
              Thank You for Uploading!
            </h3>
            <p className="text-base mb-2 max-w-md leading-relaxed" style={{ color: 'hsl(145 25% 80%)' }}>
              Your session has been submitted and is now pending review. We&apos;ll get it added to the vault as soon as possible.
            </p>
            <p className="text-sm mb-10" style={{ color: 'hsl(145 25% 65%)' }}>
              Would you like to upload another session?
            </p>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 rounded-lg font-semibold transition-colors"
                style={{
                  backgroundColor: 'transparent',
                  border: '1.5px solid hsl(145 30% 60%)',
                  color: 'hsl(145 30% 90%)',
                  minWidth: '160px',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'hsl(145 40% 20%)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
              >
                No, I&apos;m Done
              </button>
              <button
                type="button"
                onClick={handleUploadAnother}
                className="px-6 py-2.5 rounded-lg font-semibold transition-colors"
                style={{
                  backgroundColor: 'hsl(145 50% 35%)',
                  border: '1.5px solid hsl(145 50% 45%)',
                  color: 'white',
                  minWidth: '160px',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'hsl(145 50% 28%)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'hsl(145 50% 35%)'; }}
              >
                Yes, Upload Another
              </button>
            </div>
          </div>
        ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-row overflow-hidden flex-1" style={{ minHeight: 0 }}>
          {/* LEFT COLUMN */}
          <div className="flex flex-col p-4 space-y-2 overflow-y-auto upload-modal-scroll flex-1" style={{ minWidth: 0 }}>

            {/* Drop zone */}
            <div>
              <label className="label" style={{ color: 'white' }}>Upload Session File / Presentation</label>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className="rounded-xl border-2 border-dashed p-1.5 text-center cursor-pointer transition-all duration-200"
                style={{
                  borderColor: dragOver
                    ? 'hsl(150 52% 44%)'
                    : selectedFile
                    ? 'hsl(150 60% 35%)'
                    : 'hsl(145 40% 52%)',
                  backgroundColor: dragOver
                    ? 'hsl(153 62% 27% / 0.12)'
                    : selectedFile
                    ? 'hsl(150 55% 88%)'
                    : 'hsl(145 42% 94%)',
                  boxShadow: selectedFile ? '0 0 0 2px hsl(150 60% 35% / 0.3)' : 'none',
                }}
              >
                {selectedFile ? (
                  <div className="flex items-center w-full px-1">
                    <img
                      src="/assets/images/WHATSAPP_PROFILE_PHOTOS__78_-1783456237654.png"
                      alt="File icon"
                      style={{ width: '48px', height: '48px', objectFit: 'contain', flexShrink: 0, marginLeft: '-8px' }}
                    />
                    <div className="text-left flex-1 min-w-0 mx-2">
                      <p className="text-sm font-semibold truncate" style={{ color: '#111827' }}>
                        {selectedFile.name}
                      </p>
                      <p className="font-mono text-xs" style={{ color: '#374151' }}>
                        {formatFileSize(selectedFile.size)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setFileError(null); }}
                      className="transition-opacity hover:opacity-70 ml-auto"
                      style={{ background: 'transparent', border: 'none', padding: 0, flexShrink: 0, marginRight: '-8px' }}
                      title="Remove file"
                    >
                      <img
                        src="/assets/images/WHATSAPP_PROFILE_PHOTOS__47_-1783000489717.png"
                        alt="Remove file"
                        style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                      />
                    </button>
                  </div>
                ) : (
                  <>
                    <div
                      className="w-7 h-7 rounded-xl flex items-center justify-center mx-auto mb-1.5"
                      style={{ backgroundColor: 'hsl(153 62% 27% / 0.1)', border: '1px solid hsl(153 62% 27% / 0.2)' }}
                    >
                      <Upload size={14} style={{ color: 'hsl(150 52% 38%)' }} />
                    </div>
                    <p className="text-xs font-medium" style={{ color: '#1f2937' }}>
                      Drag and drop your file here
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: '#374151' }}>
                      or click to browse · .ppt, .pptx, .pdf, .mp4, .mov: max 100 MB
                    </p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".ppt,.pptx,.pdf,.mp4,.mov,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,video/mp4,video/quicktime,application/pdf"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }}
              />
              {fileError && (
                <p className="mt-1.5 text-xs font-medium" style={{ color: 'hsl(0 72% 60%)' }}>
                  {fileError}
                </p>
              )}
            </div>

            {/* Title */}
            <div>
              <label className="label" htmlFor="upload-title" style={{ color: 'white' }}>
                Session Title <span style={{ color: 'hsl(0 72% 55%)' }}>*</span>
              </label>
              <input
                id="upload-title"
                type="text"
                className="input-field"
                placeholder="E.G. Creating Your Game Face"
                {...register('title', { required: 'Please enter a title for this session' })}
                style={errors.title ? { borderColor: 'hsl(0 72% 55%)' } : {}}
              />
              {errors.title && (
                <p className="mt-1.5 text-xs font-medium" style={{ color: 'hsl(0 72% 60%)' }}>
                  {errors.title.message}
                </p>
              )}
            </div>

            {/* Session Aims */}
            <div>
              <label className="label" htmlFor="upload-session-aims" style={{ color: 'white' }}>
                Session Aims
                <span className="text-xs font-normal ml-2" style={{ color: 'white', opacity: 0.8 }}> | What are the key aims of this session?</span>
              </label>
              <textarea
                id="upload-session-aims"
                rows={5}
                className="w-full px-3 py-2 text-sm rounded-xl outline-none transition-all duration-150 resize-none"
                placeholder="E.G. To help the client regulate their nerves through box breathing."
                {...register('sessionAims')}
                style={{
                  backgroundColor: 'hsl(145 42% 94%)',
                  border: '1.5px solid hsl(145 40% 52%)',
                  color: '#416478',
                  fontFamily: 'inherit',
                  lineHeight: '1.5',
                }}
                onFocus={(e) => {
                  (e.currentTarget as HTMLTextAreaElement).style.borderColor = 'hsl(150 52% 44%)';
                  (e.currentTarget as HTMLTextAreaElement).style.boxShadow = '0 0 0 3px hsl(153 62% 30% / 0.15)';
                }}
                onBlur={(e) => {
                  (e.currentTarget as HTMLTextAreaElement).style.borderColor = 'hsl(145 40% 52%)';
                  (e.currentTarget as HTMLTextAreaElement).style.boxShadow = 'none';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const textarea = e.currentTarget as HTMLTextAreaElement;
                    const { selectionStart, selectionEnd, value } = textarea;
                    const newValue = value.substring(0, selectionStart) + '\n• ' + value.substring(selectionEnd);
                    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set;
                    nativeInputValueSetter?.call(textarea, newValue);
                    textarea.dispatchEvent(new Event('input', { bubbles: true }));
                    requestAnimationFrame(() => {
                      textarea.selectionStart = selectionStart + 3;
                      textarea.selectionEnd = selectionStart + 3;
                    });
                  }
                }}
                onFocusCapture={(e) => {
                  const textarea = e.currentTarget as HTMLTextAreaElement;
                  if (textarea.value === '') {
                    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set;
                    nativeInputValueSetter?.call(textarea, '• ');
                    textarea.dispatchEvent(new Event('input', { bubbles: true }));
                    requestAnimationFrame(() => {
                      textarea.selectionStart = 2;
                      textarea.selectionEnd = 2;
                    });
                  }
                }}
              />
            </div>

            {/* Delivery Tips */}
            <div>
              <label className="label" htmlFor="upload-delivery-tips" style={{ color: 'white' }}>
                Delivery Tips
                <span className="text-xs font-normal ml-2" style={{ color: 'white', opacity: 0.8 }}> | What do you do to make the session work well and be really enjoyable for the client?</span>
              </label>
              <textarea
                id="upload-delivery-tips"
                rows={6}
                className="w-full px-3 py-2 text-sm rounded-xl outline-none transition-all duration-150 resize-none"
                placeholder="E.G. Physically practice box breathing with the client. Do it yourself the first time, do it with them the second time, let them do it alone the third time."
                {...register('deliveryTips')}
                style={{
                  backgroundColor: 'hsl(145 42% 94%)',
                  border: '1.5px solid hsl(145 40% 52%)',
                  color: '#416478',
                  fontFamily: 'inherit',
                  lineHeight: '1.5',
                }}
                onFocus={(e) => {
                  (e.currentTarget as HTMLTextAreaElement).style.borderColor = 'hsl(150 52% 44%)';
                  (e.currentTarget as HTMLTextAreaElement).style.boxShadow = '0 0 0 3px hsl(153 62% 30% / 0.15)';
                }}
                onBlur={(e) => {
                  (e.currentTarget as HTMLTextAreaElement).style.borderColor = 'hsl(145 40% 52%)';
                  (e.currentTarget as HTMLTextAreaElement).style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Upload error */}
            {uploadError && (
              <p className="text-xs font-medium px-3 py-2 rounded-lg" style={{ color: 'hsl(0 72% 60%)', backgroundColor: 'hsl(0 72% 10%)' }}>
                {uploadError}
              </p>
            )}

            {/* Footer */}
            <div
              className="flex items-center justify-end gap-3 pt-2 border-t mt-auto"
              style={{ borderColor: 'hsl(145 40% 40%)' }}
            >
              <button
                type="button"
                onClick={onClose}
                className="btn-outline"
                style={{ color: 'white', borderColor: 'white', background: 'transparent' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUploading || selectedCategories.length === 0}
                className="btn-outline"
                style={{ color: 'white', borderColor: 'white', background: 'transparent', minWidth: '140px' }}
              >
                {isUploading ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Uploading…
                  </>
                ) : (
                  <>
                    <Upload size={15} />
                    Upload Session
                  </>
                )}
              </button>
            </div>
          </div>

          {/* DIVIDER */}
          <div className="w-px flex-shrink-0" style={{ backgroundColor: 'hsl(145 40% 40%)' }} />

          {/* RIGHT COLUMN: Tags */}
          <div
            className="flex flex-col p-3 overflow-y-auto upload-modal-scroll"
            style={{ width: '240px', flexShrink: 0 }}
          >
            <label className="label mb-0" style={{ color: 'white', fontSize: '13px' }}>
              Tags <span style={{ color: 'hsl(0 72% 55%)' }}>*</span>
            </label>
            <p className="mb-2 mt-0.5" style={{ color: 'hsl(145 30% 92%)', fontSize: '10px' }}>
              Please select all related tags as this will help others find your session. Click once to select, click again to deselect. If you need a tag that&apos;s missing, tell Matt.
            </p>
            <div
              className="rounded-xl p-2 flex-1"
              style={{ backgroundColor: 'hsl(145 42% 94%)', border: '1px solid hsl(145 40% 52%)' }}
            >
              {renderTagGroup('Psych', psychTags)}
              {renderTagGroup('Age', ageTags)}
              {renderTagGroup('Sport', sportTags)}
            </div>
          </div>
        </form>
        )}
      </div>

      {/* Confirmation Popup */}
      {showConfirm && (
        <div
          className="absolute inset-0 flex items-center justify-center z-50"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', borderRadius: 'inherit' }}
        >
          <div
            className="rounded-2xl p-8 flex flex-col items-center gap-6 shadow-2xl"
            style={{
              backgroundColor: 'hsl(145 42% 18%)',
              border: '1.5px solid hsl(145 40% 40%)',
              maxWidth: '480px',
              width: '90%',
            }}
          >
            <p
              className="text-center font-semibold leading-relaxed"
              style={{ color: 'hsl(145 30% 94%)', fontSize: '1.1rem' }}
            >
              Could someone who has never seen your session before, deliver it to 95% of the level you do with the information you have provided?
            </p>
            <div className="flex gap-4 w-full justify-center">
              <button
                type="button"
                onClick={handleCancelConfirm}
                className="px-5 py-2.5 rounded-lg font-semibold transition-colors"
                style={{
                  backgroundColor: 'transparent',
                  border: '1.5px solid hsl(145 30% 60%)',
                  color: 'hsl(145 30% 90%)',
                  minWidth: '150px',
                }}
              >
                No, Keep Editing
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="px-5 py-2.5 rounded-lg font-semibold transition-colors"
                style={{
                  backgroundColor: 'hsl(145 50% 35%)',
                  border: '1.5px solid hsl(145 50% 45%)',
                  color: 'white',
                  minWidth: '150px',
                }}
              >
                Yes, Submit Session
              </button>
            </div>
            <p
              className="text-center text-sm mt-2"
              style={{ color: 'hsl(145 25% 65%)' }}
            >
              Don&apos;t forget to check your grammar + spell check.
            </p>
          </div>
        </div>
      )}

      {/* Example Preview Modal */}
      {showExamplePreview && (
        <div
          className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
          style={{ backgroundColor: 'rgba(15, 25, 35, 0.72)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowExamplePreview(false)}
        >
          <div
            className="relative w-full rounded-2xl overflow-hidden flex flex-col my-auto min-h-0"
            style={{
              backgroundColor: 'white',
              boxShadow: '0 20px 60px rgba(0,0,0,0.22), 0 4px 16px rgba(0,0,0,0.12)',
              height: 'calc(100vh - 2rem)',
              maxHeight: 'calc(100vh - 2rem)',
              maxWidth: '82.8rem',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div
              className="flex items-center justify-between px-3 py-3 sm:px-5 sm:py-4 flex-shrink-0 gap-2"
              style={{ background: 'linear-gradient(135deg, #416478 0%, #469dbd 100%)' }}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
                >
                  <FileText size={16} style={{ color: 'white' }} />
                </div>
                <div className="min-w-0 flex items-center gap-2 flex-wrap">
                  <h2 className="text-sm font-bold leading-snug truncate" style={{ color: 'white' }}>
                    Creating Your Game Face — Example Session
                  </h2>
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded-md text-white font-bold flex-shrink-0"
                    style={{
                      fontSize: '0.55rem',
                      letterSpacing: '0.04em',
                      background: 'rgba(255,255,255,0.22)',
                      border: '1px solid rgba(255,255,255,0.4)',
                      textTransform: 'uppercase',
                    }}
                  >
                    Example
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowExamplePreview(false)}
                className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-150 flex-shrink-0"
                style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
                title="Close example preview"
                aria-label="Close example preview"
                onMouseEnter={(e) => (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255,255,255,0.25)'}
                onMouseLeave={(e) => (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255,255,255,0.15)'}
              >
                <X size={16} style={{ color: 'white' }} />
              </button>
            </div>

            {/* Modal body */}
            <div className="flex flex-1 min-h-0 overflow-hidden">
              {/* Left panel — session aims + delivery tips */}
              <div className="flex flex-col border-r flex-shrink-0 overflow-hidden" style={{ borderColor: 'hsl(215 20% 88%)', width: '40%' }}>

                {/* Session Aims */}
                <div className="px-4 pt-4 pb-3 flex flex-col min-h-0" style={{ flex: '0 0 40%' }}>
                  <div style={{ borderTop: '2px solid #416478', marginBottom: '8px' }} />
                  <p className="font-bold mb-2 flex-shrink-0" style={{ color: '#416478', fontSize: '1.125rem' }}>Session Aims</p>
                  <ul className="text-sm leading-relaxed overflow-y-auto flex-1 min-h-0" style={{ color: '#416478' }}>
                    {[
                      'Help the athlete understand what their ideal performance state feels like',
                      'Introduce the concept of a "Game Face" as a pre-performance trigger',
                      'Practise activating the Game Face using imagery and physical anchoring',
                      'Build a repeatable pre-performance routine the athlete can use independently',
                    ].map((aim, i) => (
                      <li key={i} className="mb-1.5 flex items-start gap-1.5">
                        <span className="flex-shrink-0 mt-0.5" style={{ color: '#469dbd' }}>•</span>
                        <span>{aim}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Delivery Tips */}
                <div className="px-4 pb-4 flex flex-col min-h-0" style={{ flex: '0 0 60%' }}>
                  <div style={{ borderTop: '2px solid #416478', marginBottom: '8px' }} />
                  <p className="font-bold mb-2 flex-shrink-0" style={{ color: '#416478', fontSize: '1.125rem' }}>Delivery Tips / Advice</p>
                  <ul className="text-sm leading-relaxed overflow-y-auto flex-1 min-h-0" style={{ color: '#416478' }}>
                    {[
                      'Start by asking the athlete to recall a time they performed at their very best — use this as the anchor for the session',
                      'Use slow, deliberate language during the imagery exercise. Pause often. Let them sit in the feeling',
                      'Physically demonstrate the "Game Face" posture yourself first — shoulders back, chin up, eyes forward — then invite them to mirror you',
                      'Avoid rushing the anchoring step. Repeat the physical cue (e.g. fist clench) at least 3 times during the session',
                      'End by asking them to describe their Game Face in one word — this becomes their personal trigger word',
                      'Encourage them to practise the routine daily for 2 weeks, not just before competition',
                    ].map((tip, i) => (
                      <li key={i} className="mb-1.5 flex items-start gap-1.5">
                        <span className="flex-shrink-0 mt-0.5" style={{ color: '#469dbd' }}>•</span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Right panel — example file preview placeholder */}
              <div className="min-w-0 p-3 sm:p-5 flex flex-col" style={{ width: '60%' }}>
                <div
                  className="relative w-full flex-1 min-h-0 rounded-xl flex flex-col items-center justify-center gap-4"
                  style={{
                    border: '1.5px dashed hsl(200 35% 75%)',
                    backgroundColor: 'hsl(200 30% 97%)',
                  }}
                >
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: 'hsl(200 45% 90%)', border: '1px solid hsl(200 35% 80%)' }}
                  >
                    <FileText size={28} style={{ color: '#469dbd' }} />
                  </div>
                  <div className="text-center px-6">
                    <p className="font-bold mb-1" style={{ color: '#416478', fontSize: '1rem' }}>
                      Creating Your Game Face
                    </p>
                    <p className="text-sm mb-3" style={{ color: 'hsl(215 20% 50%)' }}>
                      This is an example of a well-structured session upload. Your uploaded file (PowerPoint, PDF, or video) will appear here in the preview.
                    </p>
                    <div className="flex flex-wrap gap-1.5 justify-center">
                      {['Confidence', 'Self-Talk', '16+', 'Individual Sport'].map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center rounded-lg font-semibold"
                          style={{
                            height: '18px',
                            fontSize: '0.6rem',
                            padding: '0 7px',
                            backgroundColor: tag === '16+' ? '#7c3aed' : tag === 'Individual Sport' ? '#16a34a' : '#469dbd',
                            color: 'white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-center px-8" style={{ color: 'hsl(215 20% 60%)' }}>
                    The more detail you provide in your Session Aims and Delivery Tips, the more useful your session will be to other PEAK psychs.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
